import type { SocialPlatform } from "@/types/account";
import type { AssistantJobSocketData } from "@/types/jobs";
import type { QueryClient } from "@tanstack/vue-query";

/**
 * Invalidates assistant queries based on job type
 * @param data Job data
 * @param queryClient tanstack queryClient instance
 * @returns void
 */
export function handleDiscoveryJob(
  data: AssistantJobSocketData,
  queryClient: QueryClient
) {
  try {
    //
    // reused helpers
    //
    const assistantHomeSummaryKey = getAssistantResourceSummaryQueryKey();
    const source = data.data.source;
    const isMainAccountWebsite =
      data.jobType === "process_website" && source === "account.website";

    const isCompetitorWebsite =
      data.jobType === "process_website" && data.data.isCompetitor;

    const isCompetitorComplete = data.jobType === "account_discover_competitor";

    const isMainAccountSocialMedia =
      data.jobType === "ProcessSocialMedia" &&
      !data.data.isCompetitor &&
      socialPlatformArr.includes(data.data.source as SocialPlatform);

    const isCompetitorSocialMedia =
      data.jobType === "ProcessSocialMedia" && data.data.isCompetitor;

    if (data.status === "running") return;

    //
    // Job Handlers
    //

    // competitors
    if (isCompetitorWebsite || isCompetitorComplete || isCompetitorSocialMedia)
      handleCompetitorJobs(data, queryClient);

    // website
    if (isMainAccountWebsite) handleMainWebsiteAccountJobs(queryClient);

    // Account socials
    if (isMainAccountSocialMedia) handleMainAccountSocialJobs(data, queryClient);

    // summary
    if (
      isMainAccountWebsite ||
      isMainAccountSocialMedia ||
      isCompetitorComplete ||
      isCompetitorWebsite
    ) {
      if (data.status === "failed") return;
      queryClient.invalidateQueries({
        queryKey: assistantHomeSummaryKey,
      });
    }
  } catch (e) {
    console.error(e);
  }
}

function handleMainWebsiteAccountJobs(queryClient: QueryClient) {
  // website
  queryClient.invalidateQueries({
    queryKey: getAssistantWebsitePagesQueryKey(),
  });
  // pages
  queryClient.invalidateQueries({
    queryKey: getAssistantWebsiteDataQueryKey(),
  });
  // processing state
  queryClient.invalidateQueries({
    queryKey: getAssistantResourceStateKey("website"),
  });
}

function handleMainAccountSocialJobs(
  data: AssistantJobSocketData,
  queryClient: QueryClient
) {
  const platform = data.data.source as SocialPlatform;
  const key = data.data.key;
  const profileKey = `${platform}#${platform}#${key}#profile`;
  const postKey = `${platform}#${platform}#${key}#post`;
  // platform#platform#key#profile
  // profile
  queryClient.invalidateQueries({
    queryKey: getAssistantSocialProfileQueryKey(platform, profileKey),
  });
  // posts
  queryClient.invalidateQueries({
    queryKey: getAssistantsPostsQueryKey(platform, postKey),
  });

  // processing state
  queryClient.invalidateQueries({
    queryKey: ["assistant-data", "state"],
  });

  // reels
  if (platform === "facebook") {
    queryClient.invalidateQueries({
      queryKey: getAssistantsPostsQueryKey(
        platform,
        `${platform}#${platform}#${key}#reel`
      ),
    });
  }
}

function handleCompetitorJobs(data: AssistantJobSocketData, queryClient: QueryClient) {
  const ids = data.data.accountDataItemIds || [];
  const rootId = ids[0]?.slice(0, ids[0].lastIndexOf("#"));

  if (data.status === "complete") {
    queryClient.invalidateQueries({
      queryKey: ["account"],
      exact: true,
    });
  }
  // website
  if (rootId) {
    // landing page
    queryClient.invalidateQueries({
      queryKey: getAssistantSingleCompetitorQueryKey(rootId + "#website"),
    });
    // website
    queryClient.invalidateQueries({
      queryKey: getAssistantCompetitorPagesQueryKey(rootId + "#website"),
    });
  }
  // social
  queryClient.invalidateQueries({
    queryKey: ["assistant-social-posts"],
  });
  // profile
  queryClient.invalidateQueries({
    queryKey: ["assistant-social-profile"],
  });
  // processing state
  queryClient.invalidateQueries({
    queryKey: ["assistant-data", "state"],
  });
}

export async function handleRetryCompletion(
  data: AssistantJobSocketData,
  queryClient: QueryClient
) {
  try {
    const itemId = data?.data?.accountDataItemId;
    if (!itemId) return;
    // invalidate and refetch data on current view
    await queryClient.invalidateQueries({
      queryKey: ["assistant-data"],
    });
    const key = "subpage-retries";
    const competitorRetryKey = `retry-${itemId}`;

    const notifyVueuse = (_key: string, newValue: any) => {
      // Dispatch event to notify useSessionStorage listeners
      window.dispatchEvent(
        new CustomEvent("vueuse-storage", {
          detail: {
            key: _key,
            storageArea: sessionStorage,
            newValue,
          },
        })
      );
    };
    const subPageRetries = sessionStorage.getItem(key);
    const competitorRetryValue = sessionStorage.getItem(competitorRetryKey);
    if (!subPageRetries && !competitorRetryValue) return;

    if (competitorRetryValue) {
      sessionStorage.setItem(competitorRetryKey, "0");
      notifyVueuse(competitorRetryKey, 0);
    } else if (subPageRetries) {
      const retryList: Record<string, number> = JSON.parse(subPageRetries);
      if (!retryList?.[itemId]) return;
      retryList[itemId] = 0;
      sessionStorage.setItem(key, JSON.stringify(retryList));
      notifyVueuse(key, retryList);
    }
  } catch (e) {
    console.error("Error handling retry completion:", e);
    // do nothing
  }
}
